import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { X, ChevronDown, ChevronUp, Calculator, RotateCcw, Share2, Banknote, Save, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { blockNonNumericPaste, NumericInput } from './numericInputUtils';
import HoverBar from './HoverBar';
import InfoBox from './InfoBox';
import ModalCloseButton from './ModalCloseButton';
import DripShareModal from './DripShareModal';
import ContributionAllocationModal from './ContributionAllocationModal';
import RepinPriceButton from './RepinPriceButton';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';
import useCpiYoy from '@/hooks/useCpiYoy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CASH_KEY = '__CASH__';

function BarChart({ data, color, title, formatVal, isDark = false }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.value));
  const ceil = maxVal > 0 ? maxVal : 1;

  return (
    <div className="flex-1 min-w-0 sm:min-w-[240px]">
      <h4 className="text-sm font-bold mb-2">{title}</h4>
      <div className="flex items-end gap-1 h-40 border-b border-border/40 px-1">
        {data.map((d, i) => {
          const h = ceil > 0 ? (d.value / ceil) * 100 : 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
              <HoverBar
                label={`Y${d.year}: ${formatVal(d.value)}`}
                className="w-full max-w-[28px] rounded-t-sm transition-all duration-300"
                style={{ height: `${Math.max(h, 2)}%`, backgroundColor: color }}
                isDark={isDark}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 px-1 mt-1">
        {data.map((d, i) => (
          <div key={i} className="flex-1 text-center">
            <span className="text-[8px] font-mono text-muted-foreground">Y{d.year}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-foreground/60 dark:text-muted-foreground text-center mt-1 font-semibold">
        Range: {formatVal(Math.min(...data.map(d => d.value)))} - {formatVal(Math.max(...data.map(d => d.value)))}
      </p>
    </div>
  );
}

// Sanitize a per-stock dividend CAGR for use in the portfolio average.
//
// Updated 2026-05 to the "realistic projection" rule: take the raw
// 5-yr CAGR and divide by 10. A stock showing +42.1% 5-yr CAGR projects
// forward at +4.21%/yr. Negative CAGRs pass straight through with the
// same /10 damping (-20% 5-yr becomes -2%/yr) so the portfolio average
// honestly reflects stocks that are shrinking dividends.
//
// Only bound: 13% HARD cap on the upper end. Nothing sustains >13%
// long-term. No lower floor — negatives are legitimate projections,
// though the input clamp keeps things sensible (-13% min so users
// can't accidentally type -99 and model dividends vanishing next year).
//
// Fallback to 3% only when the CAGR is literally missing/NaN so the
// calculator still produces a reasonable seed on stocks without
// 5-yr history yet.
const DIV_GROWTH_MAX = 13;
const DIV_GROWTH_MIN = -13;

function sanitizeStockDivGrowth(cagr) {
  const n = Number(cagr);
  if (!Number.isFinite(n)) return 3;
  const damped = n / 10;
  return Math.min(DIV_GROWTH_MAX, Math.max(DIV_GROWTH_MIN, damped));
}

function clampDivGrowthInput(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.min(DIV_GROWTH_MAX, Math.max(DIV_GROWTH_MIN, n));
}

export default function PortfolioDripModal({ open, onClose, isDark }) {
  const dripScopeParams = usePortfolioScopeParams();
  const dripScopeKey = dripScopeParams.portfolio_id || 'all';
  // Phase C (May 2026) — prices are pinned at modal-open time:
  // `dripData` is fetched once via `useEffect`, and every downstream
  // calculation reads `s.price` from that frozen response. Massive
  // WebSocket price updates streaming into stock_cache do NOT mutate
  // this modal's projection while it is open. To force-refresh prices
  // the user closes and re-opens the modal.
  const [dripData, setDripData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState(10);
  const [monthlyContrib, setMonthlyContrib] = useState(250);
  const [taxRate, setTaxRate] = useState(15);
  // Two independent global growth knobs. Price growth seeds at a flat
  // 3% (a conservative long-term real-equity-return floor); Div growth
  // seeds at the portfolio's average per-stock 5-year CAGR (capped at
  // 25%) once `dripData` arrives. Per-stock overrides only affect div
  // growth — price growth is single-knob across the whole portfolio.
  const [globalGrowth, setGlobalGrowth] = useState(3);
  const [globalDivGrowth, setGlobalDivGrowth] = useState(3);
  const [enableDrip, setEnableDrip] = useState(true);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [overrides, setOverrides] = useState({});
  // Per-ticker contribution allocation (Round 67, May 2026).
  // `allocMode` is 'dollars' or 'percent'; `allocations` is a map of
  // TICKER → number, plus an optional '__CASH__' key for the user's
  // "leave it in cash" allocation. An empty map = no allocation set,
  // which makes the calc loop fall back to an even-split with leftover
  // pennies routed to the highest-yield ticker.
  const [allocMode, setAllocMode] = useState('dollars');
  const [allocations, setAllocations] = useState({});
  const [allocModalOpen, setAllocModalOpen] = useState(false);
  // Round P122c (Feb 2026) — Saved scenarios.
  const [scenarios, setScenarios] = useState([]);
  const [scenariosLoaded, setScenariosLoaded] = useState(false);
  const [savingScenario, setSavingScenario] = useState(false);
  // Snapshot of the "as first loaded" state so the user can blast every
  // tweak away with a single Reset click. Captured the moment the
  // /portfolio/drip-data fetch resolves — always the portfolio-derived
  // seeded defaults, NOT whatever the user had previously saved.
  const initialSnapshot = useRef(null);
  // Gates the auto-save effect: false until the initial fetch
  // (drip-data + drip-settings) finishes applying state, so the
  // hydrate phase doesn't immediately echo back to the server.
  const loadedRef = useRef(false);
  // Bumped by Reset to suppress the very next auto-save tick
  // (Reset already wipes the server-side record explicitly).
  const skipSaveRef = useRef(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Phase C (May 2026) — ISO timestamp of the last successful price
  // snapshot. Captured on initial load and every "Refresh price" click.
  const [pinnedAt, setPinnedAt] = useState(null);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  // Round 96n (Jul 3 2026) — Real vs Nominal projection toggle.
  // Deflates every future-dollar output (futureValue, totalDivIncome,
  // yearlyValues, yearlyIncome) by (1 + CPI YoY)^year so results
  // render in today's purchasing power. Shared localStorage key with
  // the single-stock DripCalcTab so both calculators respect the same
  // user preference.
  const cpiYoy = useCpiYoy();
  const [projectionMode, setProjectionMode] = useState(() => {
    try { return localStorage.getItem('dividrip.dripProjectionMode') || 'nominal'; } catch { return 'nominal'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dividrip.dripProjectionMode', projectionMode); } catch { /* ignore */ }
  }, [projectionMode]);
  const effectiveProjectionMode = (projectionMode === 'real' && typeof cpiYoy !== 'number') ? 'nominal' : projectionMode;

  // Re-fetch ONLY the live prices for the tickers already in dripData
  // and merge them into existing dripData.stocks. Preserves every user
  // tweak (overrides, contributions, timeline, etc.) — Phase C "refresh
  // now" path that doesn't reset the modal.
  const handleRefreshPrices = async () => {
    if (refreshingPrices || !dripData?.stocks?.length) return;
    setRefreshingPrices(true);
    try {
      const tickers = dripData.stocks.map(s => s.ticker).filter(Boolean);
      const priceMap = {};
      // Backend /stocks/batch caps 20 per call.
      for (let i = 0; i < tickers.length; i += 20) {
        const chunk = tickers.slice(i, i + 20);
        const res = await axios.get(`${API}/stocks/batch/${chunk.join(',')}`, { withCredentials: true });
        (res.data || []).forEach(s => { if (s?.ticker) priceMap[s.ticker] = s; });
      }
      setDripData(prev => {
        if (!prev?.stocks) return prev;
        const merged = prev.stocks.map(s => {
          const fresh = priceMap[s.ticker];
          if (!fresh) return s;
          return { ...s, price: fresh.price ?? s.price, dividend_rate: fresh.dividend_rate ?? s.dividend_rate };
        });
        return { ...prev, stocks: merged };
      });
      setPinnedAt(new Date().toISOString());
    } catch {
      toast.error('Could not refresh prices', { description: 'Try again in a moment.' });
    } finally {
      setRefreshingPrices(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    loadedRef.current = false;
    // Parallel fetch: portfolio data (for seeded defaults) + saved
    // settings (cross-device persistence). Settings are optional —
    // 200 with null body means nothing saved yet.
    Promise.all([
      axios.get(`${API}/portfolio/drip-data`, { params: dripScopeParams, withCredentials: true }),
      axios.get(`${API}/portfolio/drip-settings`, { withCredentials: true })
        .catch(() => ({ data: null })),
    ])
      .then(([dataRes, settingsRes]) => {
        setDripData(dataRes.data);
        setPinnedAt(new Date().toISOString());
        const stocks = dataRes.data.stocks || [];
        const saved = settingsRes?.data || null;
        // Compute the portfolio-average div CAGR — same logic as the
        // per-stock DRIP tab (capped 0..25, fallback 3 for bad data).
        let seededGlobalDiv = 3;
        let seededOverrides = {};
        if (stocks.length > 0) {
          const sum = stocks.reduce((acc, s) => acc + sanitizeStockDivGrowth(s.cagr_5yr), 0);
          const avg = sum / stocks.length;
          seededGlobalDiv = Math.min(25, Math.max(0, Number(avg.toFixed(2))));
          // Seed per-stock overrides with each stock's own sanitized
          // CAGR so the override panel starts on accurate per-ticker
          // values (not the global average). Users can still tweak.
          stocks.forEach(s => {
            seededOverrides[s.ticker] = Number(sanitizeStockDivGrowth(s.cagr_5yr).toFixed(2));
          });
        }
        // Snapshot all "first loaded" defaults so Reset can restore them.
        // Captured BEFORE applying saved settings so Reset always returns
        // to the portfolio-derived state, not the user's last tweak set.
        initialSnapshot.current = {
          timeline: 10,
          monthlyContrib: 250,
          taxRate: 15,
          globalGrowth: 3,
          globalDivGrowth: seededGlobalDiv,
          enableDrip: true,
          overrides: seededOverrides,
        };
        // Apply saved settings on top of seeded defaults if present.
        // Per-ticker overrides from the saved doc are merged into the
        // seeded map so newly added portfolio tickers still get a
        // sensible CAGR-based default while previously tweaked ones
        // keep their stored value.
        if (saved && typeof saved === 'object') {
          if (Number.isFinite(saved.timeline)) setTimeline(saved.timeline);
          if (Number.isFinite(saved.monthly_contrib)) setMonthlyContrib(saved.monthly_contrib);
          if (Number.isFinite(saved.tax_rate)) setTaxRate(saved.tax_rate);
          if (Number.isFinite(saved.global_growth)) setGlobalGrowth(saved.global_growth);
          if (Number.isFinite(saved.global_div_growth)) setGlobalDivGrowth(saved.global_div_growth);
          else setGlobalDivGrowth(seededGlobalDiv);
          if (typeof saved.enable_drip === 'boolean') setEnableDrip(saved.enable_drip);
          const mergedOverrides = { ...seededOverrides };
          if (saved.overrides && typeof saved.overrides === 'object') {
            Object.entries(saved.overrides).forEach(([k, v]) => {
              if (Number.isFinite(Number(v)) && k in mergedOverrides) {
                mergedOverrides[k] = Number(v);
              }
            });
          }
          setOverrides(mergedOverrides);
          // Hydrate contribution allocation. Only honor entries whose
          // ticker still exists in the user's portfolio (drops entries
          // for tickers they've since sold).
          if (saved.contribution_alloc_mode === 'percent' || saved.contribution_alloc_mode === 'dollars') {
            setAllocMode(saved.contribution_alloc_mode);
          }
          if (saved.contribution_alloc && typeof saved.contribution_alloc === 'object') {
            const ownedSet = new Set(stocks.map((s) => s.ticker));
            ownedSet.add(CASH_KEY);
            const filtered = {};
            Object.entries(saved.contribution_alloc).forEach(([k, v]) => {
              if (ownedSet.has(k) && Number.isFinite(Number(v)) && Number(v) > 0) {
                filtered[k] = Number(v);
              }
            });
            setAllocations(filtered);
          }
        } else {
          setGlobalDivGrowth(seededGlobalDiv);
          setOverrides(seededOverrides);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        // Defer to next tick so the state setters above flush before
        // the auto-save effect's deps fire.
        setTimeout(() => { loadedRef.current = true; }, 0);
      });
  }, [open, dripScopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore every input to the values captured the moment drip-data
  // was first fetched, and wipe the server-side record so the next
  // open starts clean (no stale saved settings rebound on top of the
  // freshly-reset state).
  const handleReset = () => {
    const snap = initialSnapshot.current;
    if (!snap) return;
    skipSaveRef.current = true;
    setTimeline(snap.timeline);
    setMonthlyContrib(snap.monthlyContrib);
    setTaxRate(snap.taxRate);
    setGlobalGrowth(snap.globalGrowth);
    setGlobalDivGrowth(snap.globalDivGrowth);
    setEnableDrip(snap.enableDrip);
    setOverrides({ ...snap.overrides });
    setAllocations({});
    setAllocMode('dollars');
    axios.delete(`${API}/portfolio/drip-settings`, { withCredentials: true })
      .catch(() => {});
  };

  // Round P122c (Feb 2026) — Scenario save/load/delete helpers.
  // The list is fetched once when the modal opens and then updated
  // optimistically after each save/delete so the dropdown stays in
  // sync without an extra round-trip.
  const fetchScenarios = async () => {
    try {
      const { data } = await axios.get(
        `${API}/portfolio/drip-scenarios`,
        { withCredentials: true },
      );
      setScenarios(data?.scenarios || []);
    } catch (_e) {
      // silent — dropdown just stays empty
    } finally {
      setScenariosLoaded(true);
    }
  };

  const saveScenario = async () => {
    if (savingScenario) return;
    // eslint-disable-next-line no-alert
    const raw = window.prompt('Name this scenario (e.g. "$250 TRIN + SCHD, 5yr"):');
    if (!raw) return;
    const name = raw.trim().slice(0, 60);
    if (!name) return;
    setSavingScenario(true);
    try {
      const { data } = await axios.post(
        `${API}/portfolio/drip-scenarios`,
        {
          name,
          snapshot: {
            timeline,
            monthly_contrib: monthlyContrib,
            tax_rate: taxRate,
            global_growth: globalGrowth,
            global_div_growth: globalDivGrowth,
            enable_drip: enableDrip,
            overrides,
            contribution_alloc_mode: allocMode,
            contribution_alloc: allocations,
          },
        },
        { withCredentials: true },
      );
      toast.success(data.created ? `Scenario “${name}” saved` : `Scenario “${name}” updated`, {
        description: 'Reload it any time from the Load menu.',
      });
      await fetchScenarios();
    } catch (err) {
      toast.error('Could not save scenario', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setSavingScenario(false);
    }
  };

  const loadScenario = (sc) => {
    const s = sc?.snapshot || {};
    skipSaveRef.current = true;
    if (Number.isFinite(s.timeline)) setTimeline(Number(s.timeline));
    if (Number.isFinite(s.monthly_contrib)) setMonthlyContrib(Number(s.monthly_contrib));
    if (Number.isFinite(s.tax_rate)) setTaxRate(Number(s.tax_rate));
    if (Number.isFinite(s.global_growth)) setGlobalGrowth(Number(s.global_growth));
    if (Number.isFinite(s.global_div_growth)) setGlobalDivGrowth(Number(s.global_div_growth));
    if (typeof s.enable_drip === 'boolean') setEnableDrip(s.enable_drip);
    if (s.overrides && typeof s.overrides === 'object') setOverrides({ ...s.overrides });
    if (s.contribution_alloc_mode === 'percent' || s.contribution_alloc_mode === 'dollars') {
      setAllocMode(s.contribution_alloc_mode);
    }
    if (s.contribution_alloc && typeof s.contribution_alloc === 'object') {
      setAllocations({ ...s.contribution_alloc });
    }
    toast.success(`Loaded “${sc.name}”`);
    // Re-enable auto-save on the next tick so the loaded state also
    // persists into the last-active drip_settings row.
    setTimeout(() => { skipSaveRef.current = false; }, 100);
  };

  const deleteScenario = async (sc) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete scenario “${sc.name}”? This can’t be undone.`)) return;
    try {
      await axios.delete(
        `${API}/portfolio/drip-scenarios/${sc.id}`,
        { withCredentials: true },
      );
      setScenarios((prev) => prev.filter((x) => x.id !== sc.id));
      toast.success(`Scenario “${sc.name}” deleted`);
    } catch (err) {
      toast.error('Could not delete scenario', {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  // Fetch scenarios once when the modal opens (lazy — no fetch on unmounted).
  useEffect(() => {
    if (open && !scenariosLoaded) fetchScenarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scenariosLoaded]);

  // Live-recompute Global Div Growth whenever any per-ticker override
  // changes. Per-spec, Global is the master value used in calculations
  // and reflects the live average of every ticker's override. Manually
  // typing a new Global value does NOT touch the visible row inputs
  // (and thus doesn't re-trigger this effect) — it simply takes over
  // until the user edits a row, at which point Global snaps back to
  // the new average.
  useEffect(() => {
    if (!dripData?.stocks?.length) return;
    // Round 97a (Feb 2026) — Average only over dividend-paying
    // tickers. Non-div stocks have no meaningful div growth and would
    // otherwise drag the average toward 0.
    const divStocks = dripData.stocks.filter(s => s.pays_dividend !== false);
    if (!divStocks.length) return;
    const values = divStocks.map(s => Number(overrides[s.ticker]));
    if (values.some(v => !Number.isFinite(v))) return; // wait for seed
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const rounded = Math.min(DIV_GROWTH_MAX, Math.max(DIV_GROWTH_MIN, Number(avg.toFixed(2))));
    setGlobalDivGrowth(prev => (prev === rounded ? prev : rounded));
  }, [overrides, dripData]);

  // Debounced cross-device auto-save: 800ms after the user stops
  // tweaking, persist the entire calculator state. Gated on
  // `loadedRef` so the hydrate phase doesn't echo back. `skipSaveRef`
  // is set by Reset to swallow the immediate post-reset save (Reset
  // already DELETEs the server doc explicitly).
  useEffect(() => {
    if (!loadedRef.current) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    const t = setTimeout(() => {
      // Only persist overrides the user has actually modified — i.e.
      // values that differ from the freshly-seeded portfolio defaults.
      // This prevents a "first time the user opens the calc before any
      // dividend-growth fetches have completed" scenario from saving
      // {AAPL: 3, MSFT: 3, ...} fallbacks and then overriding the real
      // seeded CAGRs on subsequent opens.
      const seeded = initialSnapshot.current?.overrides || {};
      const userOverrides = {};
      Object.entries(overrides).forEach(([k, v]) => {
        const seedVal = seeded[k];
        if (seedVal === undefined || Number(v) !== Number(seedVal)) {
          userOverrides[k] = v;
        }
      });
      axios.put(
        `${API}/portfolio/drip-settings`,
        {
          timeline,
          monthly_contrib: monthlyContrib,
          tax_rate: taxRate,
          global_growth: globalGrowth,
          global_div_growth: globalDivGrowth,
          enable_drip: enableDrip,
          overrides: userOverrides,
          contribution_alloc_mode: allocMode,
          contribution_alloc: allocations,
        },
        { withCredentials: true },
      ).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [timeline, monthlyContrib, taxRate, globalGrowth, globalDivGrowth, enableDrip, overrides, allocMode, allocations]);

  const results = useMemo(() => {
    if (!dripData?.stocks || dripData.stocks.length === 0) return null;

    const stocks = dripData.stocks;
    const years = Math.max(1, timeline);
    // Round 86c (May 2026) — Switched from monthly to annual
    // compounding to match the spreadsheet's Projection-tab model
    // (Ending = Start × (1 + price growth) + Contribution + DRIP).
    // For ultra-high-yield positions this is ~13 percentage points
    // less aggressive per year than the old monthly model and
    // matches what the user expects to see.
    const tax = taxRate / 100;

    // Track per-stock state. Per user spec, calculations apply the
    // single Global Div Growth + Global Price Growth values to every
    // stock — per-ticker override inputs feed into the averaged
    // global figure (recomputed on edit) but do not drive their own
    // ticker's projection independently.
    let stockStates = stocks.map(s => ({
      ticker: s.ticker,
      shares: s.shares,
      price: s.price,
      costPerShare: Number(s.cost_per_share) || s.price || 1,
      divRate: s.dividend_rate,
      frequency: s.frequency,
      priceGrowth: globalGrowth / 100,
      divGrowth: globalDivGrowth / 100,
    }));

    // Amount Invested — strictly the user's out-of-pocket money. Starts
    // at the backend-computed `total_contributions` figure (sum of
    // deposits − withdrawals + outside-portion of every buy) so
    // inside-cash recycling never gets double-counted as a fresh
    // contribution. For legacy data without inside/outside tracking,
    // the backend falls back to summing each buy's full amount, which
    // matches the pre-feature behaviour exactly.
    //
    // From here forward the projection grows Amount Invested by the
    // FULL monthly contribution every month — regardless of whether
    // each dollar gets routed to a stock or to the cash bucket. Money
    // the portfolio GENERATES (dividends, whether DRIP-reinvested or
    // sitting as dividend cash) never counts.
    let amountInvested = Number(dripData?.total_contributions);
    if (!Number.isFinite(amountInvested) || amountInvested < 0) {
      amountInvested = stockStates.reduce(
        (sum, s) => sum + s.shares * s.costPerShare,
        0,
      );
    }

    // Yield-on-Cost denominator (separate from Amount Invested per the
    // Investopedia definition shared by the user, May 2026):
    //
    //   "YOC = current dividend ÷ price initially paid for that stock"
    //   "any holding costs and additional share purchases over time
    //   must be included in the YOC calculation"
    //
    // That means YOC's denominator is the cost of shares ACTUALLY
    // PURCHASED — initial cost basis + monthly contributions that
    // bought shares. Cash-bucket allocations are NOT share purchases,
    // so they don't belong in the YOC denominator (including them
    // would artificially deflate YOC, especially with a heavy cash
    // tilt). Dividends — whether DRIP-reinvested or pocketed — are
    // money the portfolio generated, not money the user paid for
    // shares, so they don't count either.
    //
    // This intentionally diverges from `amountInvested`: when the user
    // routes some contribution to cash, the two boxes will differ.
    // The Amount Invested tooltip and the YOC tooltip both spell that
    // out so the math is transparent.
    let stockCostBasis = amountInvested; // same starting point

    const yearlyValues = [];
    const yearlyIncome = [];
    let totalContributed = 0;
    // Cash bucket — accumulates the portion of monthly contributions
    // the user explicitly routed to Cash, plus any leftover that
    // wasn't allocated to any ticker. Cash earns nothing and never
    // pays a dividend; it just gets added to the final portfolio
    // value at the end of every year. Allocation rules:
    //   • allocations empty → even split (pennies → highest-yield ticker)
    //   • mode='dollars'    → values are absolute $/mo per key
    //   • mode='percent'    → values are 0-100, applied to monthlyContrib
    //   • Special key '__CASH__' is the user's explicit cash chunk.
    //   • Unallocated leftover (under-allocation) auto-routes to cash.
    let cashBucket = 0;
    // Round P122b (Feb 2026) — Sticky flag for the yield-trap cap so
    // the UI can show a small "Trap-cap applied" indicator when the
    // projection had to clip a >20% weighted allocation yield.
    let trapCapApplied = false;
    const allocMap = allocations || {};
    const hasAlloc = Object.keys(allocMap).some(
      (k) => Number(allocMap[k]) > 0,
    );

    // Pre-compute the highest-yield ticker so the even-split fallback
    // can route leftover pennies there. Falls back to first ticker if
    // every yield is zero/missing.
    const yieldByTicker = {};
    stocks.forEach((s) => {
      const y = (Number(s.dividend_rate) || 0) / Math.max(0.01, Number(s.price) || 1);
      yieldByTicker[s.ticker] = y;
    });
    let hiYieldTicker = stocks[0]?.ticker;
    let hiY = -Infinity;
    stocks.forEach((s) => {
      const y = yieldByTicker[s.ticker];
      if (y > hiY) {
        hiY = y;
        hiYieldTicker = s.ticker;
      }
    });

    /**
     * Resolve the per-ticker dollar contribution for one YEAR.
     * Returns { perTicker: {ticker: $}, cashThisYear: $ }.
     * Round 86c (May 2026) — collapsed from monthly to annual to
     * match the spreadsheet's projection model. The user's
     * `monthlyContrib` × 12 is the annual contribution applied at
     * year-end (same cadence as the sheet's Projection tab).
     */
    const resolveAnnualAllocation = () => {
      const annualContrib = monthlyContrib * 12;
      const perTicker = {};
      stockStates.forEach((s) => { perTicker[s.ticker] = 0; });
      let cashThisYear = 0;

      if (!hasAlloc) {
        if (stockStates.length === 0) {
          cashThisYear = annualContrib;
        } else {
          const totalCents = Math.round(annualContrib * 100);
          const baseCents = Math.floor(totalCents / stockStates.length);
          const leftover = totalCents - baseCents * stockStates.length;
          stockStates.forEach((s) => { perTicker[s.ticker] = baseCents / 100; });
          if (perTicker[hiYieldTicker] != null) {
            perTicker[hiYieldTicker] += leftover / 100;
          }
        }
        return { perTicker, cashThisYear };
      }

      if (allocMode === 'percent') {
        let totalPct = 0;
        stockStates.forEach((s) => {
          const p = Number(allocMap[s.ticker]) || 0;
          perTicker[s.ticker] = (p / 100) * annualContrib;
          totalPct += p;
        });
        const cashPct = Number(allocMap[CASH_KEY]) || 0;
        cashThisYear = (cashPct / 100) * annualContrib;
        totalPct += cashPct;
        if (totalPct < 100) {
          cashThisYear += ((100 - totalPct) / 100) * annualContrib;
        }
      } else {
        // Dollars mode — the user's $ values are MONTHLY by UI design,
        // so multiply by 12 here to get the annual figure.
        let totalAllocated = 0;
        stockStates.forEach((s) => {
          const d = (Number(allocMap[s.ticker]) || 0) * 12;
          perTicker[s.ticker] = d;
          totalAllocated += d;
        });
        cashThisYear = (Number(allocMap[CASH_KEY]) || 0) * 12;
        totalAllocated += cashThisYear;
        if (totalAllocated < annualContrib) {
          cashThisYear += annualContrib - totalAllocated;
        }
      }
      return { perTicker, cashThisYear };
    };

    // ──────────────────────────────────────────────────────────────
    // Round 86c (May 2026) — Portfolio-level annual compounding loop.
    // Collapsed from a per-stock state machine to a single-blob
    // simulation that mirrors the spreadsheet's Projection tab
    // EXACTLY. The per-stock model was mathematically faithful but
    // produced explosive results because DRIP into an ultra-high
    // yielder (MSTY at 109%, EGGY at 30%+) concentrated the
    // portfolio in that yielder, so its huge dividend kept
    // reinvesting into more of itself — a real but rarely realistic
    // outcome over 5 years (yield-trap stocks usually have their
    // distributions cut or their prices crater first, which the
    // model doesn't simulate). The single-blob model uses the
    // portfolio's WEIGHTED average yield and lets that yield drift
    // naturally as price growth outpaces dividend growth, matching
    // the projection users see in the downloaded spreadsheet.
    //
    // Yield-drift formula per the sheet:
    //   yield_{n+1} = yield_n × (1 + divGrowth) / (1 + priceGrowth)
    //
    // Per-year recipe:
    //   1. annualDiv  = portfolioValue × yield
    //   2. afterTax   = annualDiv × (1 - tax)
    //   3. DRIP-on  : portfolioValue += afterTax (reinvested)
    //      DRIP-off: cashBucket   += afterTax (sits as cash)
    //   4. portfolioValue *= (1 + priceGrowth)
    //   5. yield drifts via the formula above
    //   6. portfolioValue += annualContribution (split per allocation
    //      so the cashBucket gets its share)
    // ──────────────────────────────────────────────────────────────
    const priceGrowth = globalGrowth / 100;
    const divGrowth = globalDivGrowth / 100;
    // Initial portfolio value = sum of (shares × current price). Cash
    // bucket starts at zero. Yield is the income-weighted aggregate.
    let portfolioValue = stockStates.reduce((s, st) => s + st.shares * st.price, 0);
    const initialAnnualDividends = stockStates.reduce(
      (s, st) => s + st.shares * st.divRate, 0,
    );
    let portfolioYield = portfolioValue > 0 ? initialAnnualDividends / portfolioValue : 0;
    // Stash the per-stock states so the Per-Security Div Growth panel
    // (and any other UI relying on per-stock data) still has access
    // to its initial values. The state machine itself no longer
    // mutates these — the projection runs purely off `portfolioValue`,
    // `portfolioYield`, and `cashBucket`.
    void stockStates;

    for (let y = 1; y <= years; y++) {
      // 1. Annual gross dividend on current portfolio value.
      const annualGross = portfolioValue * portfolioYield;
      const annualAfterTax = annualGross * (1 - tax);

      // 2. DRIP on / off.
      if (enableDrip) {
        portfolioValue += annualAfterTax;
      } else {
        cashBucket += annualAfterTax;
      }

      // 3. Annual price growth on the (already-DRIP-updated) value.
      portfolioValue *= (1 + priceGrowth);

      // 4. Yield drifts year-over-year — divGrowth on the per-share
      //    dividend, priceGrowth on the per-share price.
      portfolioYield = portfolioYield * (1 + divGrowth) / (1 + priceGrowth);

      // 5. Annual contribution — split per allocation. Cash share
      //    routes to the cashBucket; stock shares add to portfolio
      //    value at post-growth price (no reinvestment needed since
      //    we're operating at portfolio level now).
      //
      // Round P122 (Feb 2026) — Per-ticker yield-aware blending.
      // The old code just summed perTicker into a single `stockChunk`
      // and added it to `portfolioValue`, which made high-yield vs.
      // low-yield picks numerically identical in the projection (the
      // aggregate model uses `portfolioValue * portfolioYield`, so if
      // the same $ goes in, the same $ comes out no matter which
      // ticker received it). The user rightly flagged that as "the
      // calculator ignores my allocation changes".
      //
      // Fix: compute the allocation-weighted current yield of the
      // per-ticker split, then blend it into the aggregate yield in
      // proportion to how much of the year's stockChunk it
      // represents. Allocating to TRIN (12% yield) now nudges the
      // portfolio's forward yield UP, and allocating to SCHD (3%)
      // nudges it DOWN — matching the intuitive "my picks matter"
      // model without reintroducing the runaway concentration risk
      // of the old per-stock state machine.
      if (monthlyContrib > 0) {
        const annualContrib = monthlyContrib * 12;
        const { perTicker, cashThisYear } = resolveAnnualAllocation();
        totalContributed += annualContrib;
        amountInvested += annualContrib;
        cashBucket += cashThisYear;
        // Sum of perTicker = annualContrib − cashThisYear, which is
        // the dollar amount going into "stocks" this year.
        const stockChunk = Object.values(perTicker).reduce((s, v) => s + (v || 0), 0);
        if (stockChunk > 0) {
          // Allocation-weighted current yield of the picks receiving
          // this year's contribution. Falls back to portfolio yield
          // if the tickers have no measurable current yield.
          let weightedAllocYield = 0;
          Object.entries(perTicker).forEach(([tkr, amt]) => {
            const y = yieldByTicker[tkr] || 0;
            weightedAllocYield += (Number(amt) || 0) * y;
          });
          weightedAllocYield = weightedAllocYield / stockChunk;
          // Round P122b (Feb 2026) — Yield-trap soft cap. Ultra-high
          // yielders (EGGY 30%+, MSTY 100%+, YieldMax-style covered-
          // call funds) rarely hold their headline yield for even
          // one full year — payouts get cut or NAV craters. Assuming
          // a 30% yield holds for 5-10 years in the projection is
          // fantasy. Clip the *contribution's* weighted yield at 20%
          // so the model never inflates future dividends based on
          // yield-trap arithmetic. Existing portfolio positions are
          // untouched (that's the user's real yield today, not
          // hypothetical). A one-time flag lets the header show the
          // "trap-cap applied" chip so the user knows what happened.
          const TRAP_CAP = 0.20;
          if (weightedAllocYield > TRAP_CAP) {
            weightedAllocYield = TRAP_CAP;
            trapCapApplied = true;
          }
          // Blend into aggregate: dollar-weighted mean.
          const preValue = portfolioValue;
          portfolioValue += stockChunk;
          if (portfolioValue > 0) {
            portfolioYield = (
              (preValue * portfolioYield) + (stockChunk * weightedAllocYield)
            ) / portfolioValue;
          }
          stockCostBasis += stockChunk;
        }
      }

      const yearEndTotalValue = portfolioValue + cashBucket;
      yearlyValues.push({ year: y, value: Math.round(yearEndTotalValue) });
      yearlyIncome.push({ year: y, value: Math.round(annualAfterTax) });
    }

    const finalValue = yearlyValues[yearlyValues.length - 1]?.value || 0;
    const finalIncome = yearlyIncome[yearlyIncome.length - 1]?.value || 0;
    // Yield-on-Cost — strictly per Investopedia's definition (article
    // shared by the user May 2026): annual GROSS dividends at year N
    // ÷ cost of shares actually purchased.
    //   • Numerator is gross (pre-tax). `finalIncome` is after-tax,
    //     so we gross it back up by /(1-tax) to get pre-tax annual
    //     dividends — that's the figure the article means by
    //     "current dividend".
    //   • Denominator is `stockCostBasis` (initial cost basis + every
    //     monthly contribution dollar that was used to buy shares).
    //     Cash-bucket dollars and reinvested dividends are explicitly
    //     excluded.
    const finalYoC = stockCostBasis > 0
      ? Number(((finalIncome / (1 - tax)) / stockCostBasis * 100).toFixed(2))
      : 0;

    // Round 96n (Jul 3 2026) — apply real-dollar deflator when the
    // user has flipped the toggle. Uses (1 + CPI YoY)^year to convert
    // every future-dollar output back to today's purchasing power.
    // `currentValue`, `amountInvested`, `stockCostBasis` stay nominal
    // — they represent dollars-in-hand today or actually-contributed
    // dollars over time, not future dollars.
    const useReal = effectiveProjectionMode === 'real' && typeof cpiYoy === 'number';
    const cpiRate = useReal ? (cpiYoy / 100) : 0;
    const deflate = (v, year) => useReal ? v / Math.pow(1 + cpiRate, year) : v;
    const adjYearlyValues = yearlyValues.map((d) => ({ ...d, value: Math.round(deflate(d.value, d.year)) }));
    const adjYearlyIncome = yearlyIncome.map((d) => ({ ...d, value: Math.round(deflate(d.value, d.year)) }));
    const adjFinalValue = adjYearlyValues[adjYearlyValues.length - 1]?.value || 0;
    const adjFinalIncome = adjYearlyIncome[adjYearlyIncome.length - 1]?.value || 0;

    return {
      currentValue: dripData.total_value,
      futureValue: adjFinalValue,
      portfolioYield: dripData.portfolio_yield,
      totalDivIncome: adjFinalIncome,
      yearlyValues: adjYearlyValues,
      yearlyIncome: adjYearlyIncome,
      cashBucket: Math.round(useReal ? cashBucket / Math.pow(1 + cpiRate, years) : cashBucket),
      amountInvested: Math.round(amountInvested),
      stockCostBasis: Math.round(stockCostBasis),
      yieldOnCost: finalYoC,
      // Nominal originals for the disclaimer / share modal.
      nominalFutureValue: finalValue,
      nominalTotalDivIncome: finalIncome,
      isReal: useReal,
      cpiUsed: useReal ? cpiYoy : null,
      // Round P122b — surfaced to the header so a yield-trap chip
      // can render when EGGY/MSTY-style allocations got clipped.
      trapCapApplied,
    };
  }, [dripData, timeline, monthlyContrib, taxRate, globalGrowth, globalDivGrowth, enableDrip, allocMode, allocations, effectiveProjectionMode, cpiYoy]);

  if (!open) return null;

  const fmt = (v) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtDec = (v) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-4" data-testid="drip-modal-overlay">
      <div className="bg-card border rounded-xl shadow-2xl w-full max-w-[720px] max-h-[90vh] overflow-y-auto overflow-x-hidden pb-24 sm:pb-4" data-testid="drip-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold">Portfolio Dividend Calculator</h2>
          </div>
          <div className="flex items-center gap-2">
            {/* Phase C (May 2026) — pin/re-pin live prices without
                resetting the user's tweaks. Hidden until dripData
                loads so it doesn't appear during the initial spinner. */}
            {dripData?.stocks?.length > 0 && (
              <RepinPriceButton
                onClick={handleRefreshPrices}
                pinnedAt={pinnedAt}
                busy={refreshingPrices}
                testId="drip-modal-refresh-price"
              />
            )}
            <ModalCloseButton onClick={onClose} testId="drip-modal-close" />
          </div>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Reset + Share row — single-click reset restores every
                  field to the initial-loaded values; Share opens a
                  branded, screenshot-safe summary card. */}
              <div className="flex justify-between items-center -mt-1 -mb-2 gap-2 flex-wrap">
                <button
                  onClick={() => setShareOpen(true)}
                  disabled={!results}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="drip-share-open-btn"
                  title="Capture a branded summary card of this projection (just the card — not your whole screen)"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Share projection
                </button>

                {/* Round 96n (Jul 3 2026) — Real vs Nominal projection
                    toggle. Deflates every future-dollar output by
                    CPI YoY^year so users see today's purchasing power
                    instead of pretty-but-inflated future balances. */}
                <div className="flex items-center gap-1.5" data-testid="portfolio-drip-projection-mode-toggle">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground hidden sm:inline">Show in:</span>
                  <div className="flex items-center bg-muted rounded-md p-0.5 border border-border/60" role="tablist" aria-label="Projection dollar basis">
                    <button
                      type="button"
                      onClick={() => setProjectionMode('nominal')}
                      className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${effectiveProjectionMode === 'nominal' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      title="Show projections in NOMINAL future dollars — raw account balance at the horizon, ignoring inflation."
                      data-testid="portfolio-drip-proj-nominal"
                    >
                      Nominal $
                    </button>
                    <button
                      type="button"
                      onClick={() => setProjectionMode('real')}
                      disabled={typeof cpiYoy !== 'number'}
                      className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${effectiveProjectionMode === 'real' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'} disabled:opacity-40 disabled:cursor-not-allowed`}
                      title={
                        typeof cpiYoy === 'number'
                          ? `Show projections in TODAY'S dollars — future-dollar outputs discounted by (1 + ${cpiYoy.toFixed(2)}%)^year.`
                          : "Real-dollar view unavailable — CPI YoY still loading."
                      }
                      data-testid="portfolio-drip-proj-real"
                    >
                      Today&#39;s $ {typeof cpiYoy === 'number' ? `(−${cpiYoy.toFixed(1)}%/yr)` : ''}
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-primary/5"
                  data-testid="drip-reset-btn"
                  title="Reset every input back to the original loaded values"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset to defaults
                </button>
              </div>

              {/* Input Fields — split into two semantic rows.
                  Row 1: Timeline / Monthly Contribution / Tax Rate
                  Row 2: Global Price Growth + Global Div Growth
                  Per-stock overrides below only adjust div growth. */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Timeline (Years)</label>
                  <NumericInput min={1} max={50} integer value={timeline} onChange={setTimeline}
                    className="w-full h-10 rounded-lg border bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="drip-timeline-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Monthly Contribution ($)</label>
                  {/*
                    Allocation button — replaces the old plain number
                    input. Shows the current $ amount and a one-line
                    summary of the allocation ("Even split" when no
                    custom allocation is set, "$amounts • cash" when
                    one is). Clicking opens ContributionAllocationModal
                    where the user can split the contribution across
                    tickers (Dollars/Percent) plus a cash bucket.
                  */}
                  <button
                    type="button"
                    onClick={() => setAllocModalOpen(true)}
                    className="w-full h-10 rounded-lg border bg-background px-3 font-mono text-sm text-left flex items-center justify-between gap-2 hover:bg-muted/40 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="drip-monthly-contrib-button"
                    title="Click to allocate this contribution across the tickers in your portfolio (or set a cash bucket)."
                  >
                    <span className="font-bold text-foreground truncate">
                      ${(monthlyContrib || 0).toLocaleString()}/mo
                    </span>
                    <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">
                      {Object.keys(allocations || {}).some((k) => Number(allocations[k]) > 0)
                        ? `Custom · ${allocMode === 'percent' ? '%' : '$'}`
                        : 'Even split'}
                    </span>
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Tax Rate (%)</label>
                  <NumericInput min={0} max={100} value={taxRate} onChange={setTaxRate}
                    className="w-full h-10 rounded-lg border bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="drip-tax-rate-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Global Price Growth (%/yr)</label>
                  <NumericInput min={-50} max={50} allowNegative value={globalGrowth} onChange={setGlobalGrowth}
                    className="w-full h-10 rounded-lg border bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="drip-global-growth-input"
                    title="Annual price-appreciation rate applied to every stock in the portfolio. Defaults to 3%. Accepts negative values to model price decay (e.g. -3% for a stock you expect to decline)." />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Global Div Growth (%/yr)</label>
                  <NumericInput
                    min={DIV_GROWTH_MIN}
                    max={DIV_GROWTH_MAX}
                    step="0.1"
                    allowNegative
                    value={globalDivGrowth}
                    onChange={(n) => {
                      // Mirror the previous toast warnings when the
                      // raw input lands outside the [MIN, MAX] band.
                      // NumericInput clamps for us, so we just need to
                      // re-emit the warning for UX.
                      if (n >= DIV_GROWTH_MAX) {
                        toast.warning(`Dividend growth capped at ${DIV_GROWTH_MAX}%/yr`, {
                          description: "No stock sustainably grows its dividend above 13% long-term — we cap here to keep projections realistic.",
                          duration: 4500,
                        });
                      } else if (n <= DIV_GROWTH_MIN) {
                        toast.warning(`Dividend growth floored at ${DIV_GROWTH_MIN}%/yr`, {
                          description: "Negative growth below -13%/yr models a near-instant dividend collapse — floored here to keep projections sane.",
                          duration: 4500,
                        });
                      }
                      setGlobalDivGrowth(n);
                    }}
                    className="w-full h-10 rounded-lg border bg-background px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    data-testid="drip-global-div-growth-input"
                    title={`Master annual dividend-growth rate (%/yr) used in every stock's projection. Derived from each stock's 5-yr CAGR ÷ 10, then averaged. Range: ${DIV_GROWTH_MIN}% to ${DIV_GROWTH_MAX}% — the 13% ceiling reflects the reality that no company grows its dividend faster than that long-term. Negatives are legitimate projections for companies that are shrinking their payout.`} />
                </div>
                {/* Enable DRIP + Save/Load scenarios — same row so the
                    scenario controls sit AFTER all inputs (a natural
                    "commit these settings" spot) and no longer push
                    Reset-to-defaults down when they render. Left:
                    checkbox. Right: Save + Load (only visible when
                    ≥1 scenario exists). Wraps on mobile. */}
                <div className="sm:col-span-2 flex items-center justify-between gap-2 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="drip-enable-checkbox">
                    <input type="checkbox" checked={enableDrip} onChange={e => setEnableDrip(e.target.checked)}
                      className="w-4 h-4 rounded border-2 border-primary text-primary accent-primary" />
                    <span className="text-sm font-medium">Enable DRIP</span>
                  </label>
                  <div className="flex items-center gap-1.5" data-testid="drip-scenarios-bar">
                    <button
                      type="button"
                      onClick={saveScenario}
                      disabled={savingScenario || !dripData}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors disabled:opacity-50"
                      data-testid="drip-save-scenario-btn"
                      title="Save the current calculator inputs as a named scenario you can reload later."
                    >
                      <Save className="w-3 h-3" />
                      Save scenario
                    </button>
                    {scenarios.length > 0 && (
                      <div className="relative group">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md bg-muted hover:bg-muted/70 text-foreground transition-colors"
                          data-testid="drip-load-scenario-toggle"
                          title="Reload a previously saved scenario."
                        >
                          Load ({scenarios.length}) <ChevronDown className="w-3 h-3" />
                        </button>
                        <div
                          className="hidden group-hover:block group-focus-within:block absolute right-0 top-full mt-1 w-64 max-h-64 overflow-y-auto bg-popover border border-border rounded-md shadow-lg z-20"
                          data-testid="drip-load-scenario-menu"
                        >
                          {scenarios.map((sc) => (
                            <div
                              key={sc.id}
                              className="flex items-center gap-1 px-2 py-1.5 hover:bg-muted/50 text-[11px]"
                              data-testid={`drip-scenario-row-${sc.id}`}
                            >
                              <button
                                type="button"
                                onClick={() => loadScenario(sc)}
                                className="flex-1 text-left truncate hover:text-primary"
                                title={`${sc.name} · saved ${String(sc.updated_at || '').slice(0, 10)}`}
                                data-testid={`drip-scenario-load-${sc.id}`}
                              >
                                {sc.name}
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteScenario(sc)}
                                className="text-muted-foreground hover:text-rose-500 transition-colors p-0.5"
                                title={`Delete “${sc.name}”`}
                                data-testid={`drip-scenario-delete-${sc.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Round 86 (Feb 2026) — High-yield-with-DRIP reality
                  check. When the portfolio yield is unusually high
                  (>15%), DRIP-reinvesting at that constant rate for
                  10+ years produces compounding numbers that look
                  great on paper but are unrealistic in practice
                  (high yields almost always decay as either prices
                  recover or distributions get cut). Surface a
                  one-liner so users don't think DRIP guarantees
                  millionaire-tier returns from a battered position. */}
              {enableDrip && dripData?.portfolio_yield && dripData.portfolio_yield > 15 && (
                <div
                  className="rounded-lg border border-amber-300/70 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200 leading-snug"
                  data-testid="drip-high-yield-warning"
                >
                  <strong>Reality check:</strong> your current portfolio yield is{' '}
                  <span className="font-mono font-bold">{dripData.portfolio_yield.toFixed(2)}%</span>. The
                  calculator assumes this yield holds steady — in real life, ultra-high
                  yields rarely persist for {timeline} years (prices recover OR
                  distributions get cut). For a more realistic projection, try
                  setting price growth to <strong>10–15%</strong> (a partial recovery)
                  and div growth to <strong>-1% to -3%</strong> (distribution decay).
                </div>
              )}

              {/* Summary Cards — Row 1: existing 4 boxes (Current /
                  Value in N / Portfolio Yield / Total Div Income). On
                  phone collapses to 2 columns; on desktop stays 4-wide. */}
              {results && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border bg-background p-3" data-testid="drip-current-portfolio">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Current Portfolio</p>
                    <p className="text-lg font-bold font-mono mt-1">{fmtDec(results.currentValue)}</p>
                  </div>
                  <div className="rounded-lg border bg-blue-50 dark:bg-blue-950/30 p-3" data-testid="drip-future-value">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Value in {timeline} Yrs
                      {results.isReal && <span className="ml-1 normal-case tracking-normal text-[9px] text-blue-700 dark:text-blue-300" title={`Deflated by CPI YoY of ${results.cpiUsed.toFixed(2)}%/yr. Nominal: $${results.nominalFutureValue.toLocaleString()}`}>(today&#39;s $)</span>}
                    </p>
                    <p className="text-lg font-bold font-mono mt-1 text-blue-600 dark:text-blue-400">{fmtDec(results.futureValue)}</p>
                  </div>
                  <InfoBox
                    title="Portfolio Yield"
                    body={`Annual dividends ÷ portfolio market value at year ${timeline}. This is the "current yield" of your projected portfolio — what it would pay relative to its market price at that moment in time. Compare with Yield on Cost below to see what your actual deposits are earning.`}
                    className="rounded-lg border bg-amber-50 dark:bg-amber-950/30 p-3"
                    testId="drip-portfolio-yield"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Portfolio Yield</p>
                    <p className="text-lg font-bold font-mono mt-1 text-amber-600 dark:text-amber-400">{results.portfolioYield}%</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 italic">
                      vs. market value
                    </p>
                    {/* Round P122b (Feb 2026) — Trap-cap chip fires
                        when the projection had to clip a >20%
                        weighted allocation yield. Signals that raw
                        allocation math would have over-projected. */}
                    {results.trapCapApplied && (
                      <div
                        className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-300/60 dark:border-rose-700/60"
                        title="One or more allocations point at a ticker with a >20% headline yield. Ultra-high yielders (YieldMax-style covered-call funds, MSTY, EGGY) rarely hold that yield for even a full year — payouts get cut or NAV craters. The projection clips the allocation yield at 20% so future income isn't inflated by yield-trap arithmetic."
                        data-testid="drip-trap-cap-chip"
                      >
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Trap-cap applied
                      </div>
                    )}
                  </InfoBox>
                  <div className="rounded-lg border bg-green-50 dark:bg-green-950/30 p-3" data-testid="drip-total-div-income">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Annual Div Income
                      {results.isReal && <span className="ml-1 normal-case tracking-normal text-[9px] text-green-700 dark:text-green-300" title={`Deflated by CPI YoY of ${results.cpiUsed.toFixed(2)}%/yr. Nominal: $${results.nominalTotalDivIncome.toLocaleString()}`}>(today&#39;s $)</span>}
                    </p>
                    <p className="text-lg font-bold font-mono mt-1 text-green-600 dark:text-green-400">{fmtDec(results.totalDivIncome)}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 italic">
                      Year {timeline}, after tax
                    </p>
                  </div>
                </div>
              )}

              {/* Summary Cards — Row 2: Yield on Cost + Amount Invested.
                  Both wrapped in InfoBox so hover (desktop) or tap
                  (mobile) shows the explainer popup. */}
              {results && (
                <div className="grid grid-cols-2 gap-3">
                  <InfoBox
                    title="Yield on Cost (YoC)"
                    body={`Per the standard dividend-investing definition: current annual dividend ÷ price you actually paid for the shares. Denominator = starting cost basis + every monthly contribution dollar that was used to BUY a share by year ${timeline}. Cash-bucket allocations and dividends generated by the portfolio are NOT counted in the denominator (they aren't "share purchases"). Long-term holders of dividend-growers can see YoC climb above 100% — that's not possible with bonds.`}
                    className="rounded-lg border bg-fuchsia-50 dark:bg-fuchsia-950/30 p-3"
                    testId="drip-yield-on-cost"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Yield on Cost</p>
                    <p className="text-lg font-bold font-mono mt-1 text-fuchsia-600 dark:text-fuchsia-400">
                      {Number(results.yieldOnCost).toFixed(2)}%
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 italic">
                      vs. share cost basis
                    </p>
                  </InfoBox>
                  <InfoBox
                    title="Amount Invested"
                    body={`Out-of-pocket money only: starting cost basis + every monthly contribution you've made by year ${timeline}, whether routed to a stock OR sitting in the cash bucket. Money the portfolio generates — dividends, whether reinvested or pocketed as cash — never counts here. Note: this differs slightly from the YoC denominator above, which (per the standard YoC definition) excludes cash-bucket dollars.`}
                    className="rounded-lg border bg-sky-50 dark:bg-sky-950/30 p-3"
                    testId="drip-amount-invested"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Amount Invested</p>
                    <p className="text-lg font-bold font-mono mt-1 text-sky-600 dark:text-sky-400">{fmtDec(results.amountInvested)}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 italic">
                      Cost basis + contributions
                    </p>
                  </InfoBox>
                </div>
              )}

              {/* Cash bucket footnote — only renders when the user has
                  routed any of their monthly contribution into cash via
                  the allocation modal (or the calc auto-cashed an
                  unallocated remainder). The dollars are already
                  included in `futureValue`; this pill just makes the
                  composition transparent. */}
              {results && results.cashBucket > 0 && (
                <div
                  className="rounded-lg border-2 border-dashed border-amber-300/60 dark:border-amber-700/60 bg-amber-50/40 dark:bg-amber-950/20 p-3 flex items-start gap-3"
                  data-testid="drip-cash-bucket"
                >
                  <Banknote className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Of which · Cash
                    </p>
                    <p className="text-base font-bold font-mono text-amber-700 dark:text-amber-300">
                      {fmtDec(results.cashBucket)}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      Sitting in cash, earning nothing. Set per allocation.
                    </p>
                  </div>
                </div>
              )}

              {/* Per-Security Growth Rate Overrides */}
              {dripData?.stocks?.length > 0 && (
                <div>
                  <button
                    onClick={() => setOverridesOpen(!overridesOpen)}
                    className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                    data-testid="drip-overrides-toggle"
                  >
                    {overridesOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronDown className="w-4 h-4 -rotate-90" />}
                    Per-Security Div Growth Overrides (click to expand)
                  </button>

                  {overridesOpen && (
                    <div className="mt-3 max-h-48 overflow-y-auto space-y-2 border rounded-lg p-3" data-testid="drip-overrides-list">
                      <p className="text-[10px] text-muted-foreground italic mb-1">
                        Each ticker is seeded with its own 5-year dividend CAGR ÷ 10 (e.g. +42% 5-yr → +4.2%/yr). Range: {DIV_GROWTH_MIN}% to {DIV_GROWTH_MAX}%/yr — 13% is the hard ceiling because no company sustains faster growth long-term, and negatives honestly reflect stocks whose dividends are shrinking. Editing any row recomputes the live average and pushes it into the Global Div Growth field above.
                      </p>
                      {/* Round 97a (Feb 2026) — Hide non-dividend
                          tickers from the per-security div growth
                          override list. They have no dividend to
                          grow, so a 0%/yr override row is just
                          visual noise. */}
                      {dripData.stocks.filter(s => s.pays_dividend !== false).map(s => (
                        <div key={s.ticker} className="flex items-center justify-between gap-4">
                          <div>
                            <span className="text-sm font-bold">{s.ticker}</span>
                            <span className="text-xs text-muted-foreground ml-2">{s.frequency}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Div Growth %/yr</span>
                            <NumericInput
                              min={DIV_GROWTH_MIN} max={DIV_GROWTH_MAX} step="0.1" allowNegative
                              value={overrides[s.ticker] ?? globalDivGrowth}
                              onChange={(n) => {
                                if (n >= DIV_GROWTH_MAX) {
                                  toast.warning(`${s.ticker} div growth capped at ${DIV_GROWTH_MAX}%/yr`, {
                                    description: "No stock sustainably grows its dividend above 13% long-term.",
                                    duration: 4000,
                                  });
                                } else if (n <= DIV_GROWTH_MIN) {
                                  toast.warning(`${s.ticker} div growth floored at ${DIV_GROWTH_MIN}%/yr`, {
                                    description: "Negative growth below -13%/yr models a near-instant dividend collapse.",
                                    duration: 4000,
                                  });
                                }
                                setOverrides(prev => ({ ...prev, [s.ticker]: n }));
                              }}
                              className="w-20 h-8 rounded-md border bg-background px-2 font-mono text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary"
                              data-testid={`drip-override-${s.ticker}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Charts */}
              {results && (
                <div className="flex flex-col sm:flex-row gap-4">
                  <BarChart
                    data={results.yearlyValues}
                    color="#3b82f6"
                    title="Portfolio Value Growth"
                    formatVal={fmt}
                    isDark={isDark}
                  />
                  <BarChart
                    data={results.yearlyIncome}
                    color="#22c55e"
                    title="Annual Dividend Income (After Tax)"
                    formatVal={fmt}
                    isDark={isDark}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <DripShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        variant="portfolio"
        payload={{
          holdings: dripData?.stocks?.length || 0,
          currentValue: results?.currentValue,
          futureValue: results?.futureValue,
          portfolioYield: results?.portfolioYield,
          futureIncome: results?.totalDivIncome,
          timeline,
          monthlyContrib,
          priceGrowth: globalGrowth,
          divGrowth: globalDivGrowth,
          taxRate,
          drip: enableDrip,
        }}
      />

      {/* Contribution allocation modal — opens when the Monthly
          Contribution button is clicked. Lets the user split the
          monthly $ amount across owned tickers + a cash bucket via
          either Dollars or Percent. Persists to drip_settings via
          the existing auto-save effect once Done commits. */}
      <ContributionAllocationModal
        open={allocModalOpen}
        onClose={() => setAllocModalOpen(false)}
        monthlyContrib={monthlyContrib}
        initialMode={allocMode}
        initialAllocations={allocations}
        stocks={(dripData?.stocks || [])
          // Round 97a (Feb 2026) — Drop non-dividend tickers from the
          // allocation list. The DRIP calculator is a dividend-income
          // tool; allocating monthly buys into a non-div stock would
          // show a contribution amount that produces zero forward
          // dividends, which is misleading. Hiding them keeps the
          // dropdown focused on the dividend portion of the portfolio.
          .filter((s) => s.pays_dividend !== false)
          .map((s) => ({
            ticker: s.ticker,
            name: s.name,
            yield: (Number(s.dividend_rate) || 0) / Math.max(0.01, Number(s.price) || 1) * 100,
          }))}
        onSave={({ mode, allocations: next, monthly_contrib }) => {
          setAllocMode(mode);
          setAllocations(next);
          if (Number.isFinite(monthly_contrib) && monthly_contrib >= 0) {
            setMonthlyContrib(monthly_contrib);
          }
        }}
      />
    </div>
  );
}
